import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LinkProps } from 'next/link';
import * as React from 'react';

import { cn } from '~/lib/utils';

import { Spinner } from './spinner';
import { TransitionLink } from '../transition-link/transition-link';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'size-9'
      },
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'rounded-[6px] md:rounded-[10px] border border-input bg-background shadow-sm hover:bg-background/50 hover:text-primary',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'hover:text-primary underline-offset-4 underline p-0'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

type ButtonBaseProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'href'
> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  } & {
    isLoading?: boolean
  }

type ButtonLinkProps = ButtonBaseProps &
  Omit<LinkProps, 'onClick'> & {
    variant: 'link'
    external?: boolean
    viewTransition?: boolean
  }

type ButtonDefaultProps = {
  variant?: Exclude<VariantProps<typeof buttonVariants>['variant'], 'link'>
} & ButtonBaseProps

export type ButtonProps = ButtonLinkProps | ButtonDefaultProps

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size,
      isLoading,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isViewTransition = 'viewTransition' in props;
    const isExternal = 'external' in props && props.external;
    if (isExternal) {
      delete props.external;
    }
    if (isViewTransition) {
      delete props.viewTransition;
    }
    return variant === 'link' && 'href' in props
? (
      <TransitionLink
        href={props.href.toString()}
        target={props.external ? '_blank' : undefined}
      >
        <Comp
          className={cn(buttonVariants({ size, className, variant }))}
          ref={ref}
          type="button"
          {...props}
        >
          {isLoading ? <Spinner className="mx-auto" /> : props.children}
        </Comp>
      </TransitionLink>
    )
: (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {isLoading ? <Spinner className="mx-auto" /> : props.children}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
